import { Link, useLocation } from '@remix-run/react';
import { GQLColourWithGroupsFragment, GQLProductSideAvailableDecorationType, GQLStoreFragment } from '@theprintbar/graphql';
import {
	DesignParamKeys,
	DesignParams,
	cn,
	getCurrencyString,
	getPartnerPortalPrice,
	getSearchString,
	getSortedColours,
} from '@theprintbar/shared';
import { useState } from 'react';
import { CustomColourIconHandler, ProductColourSwatch } from '..';
import { ProductTileImage, ProductTileImageProps } from './ProductTileImage';

interface ProductTileProps extends ProductTileImageProps {
	store?: GQLStoreFragment | null;
	className?: string;
	calculator?: string;
	params?: DesignParams;
	colourGroup?: string;
	hideColour?: boolean;
	hideCompare?: boolean;
	hidePrice?: boolean;
	hideBrand?: boolean;
	hideMore?: boolean;
	hideLink?: boolean;
	hideNonSameday?: boolean;
	hideLightningBolt?: boolean;
	reloadDocument?: boolean;
	customColourIconHandler?: CustomColourIconHandler;
	onClick?: () => void;
}

export function ProductTile({
	product,
	store,
	hideColour,
	hidePrice,
	className,
	params,
	colourGroup,
	webDomain,
	productPath,
	hideBrand,
	hideLink,
	hideNonSameday,
	reloadDocument,
	customColourIconHandler,
	hideLightningBolt,
	onClick,
	...ImageProps
}: ProductTileProps) {
	const { name, brand, colours } = product;
	const location = useLocation();
	const [activeColour, setColour] = useState<string | undefined>(
		colourGroup
			? getSortedColours(colours).find(({ colour }) => {
					return !!(colour as GQLColourWithGroupsFragment)?.groups?.find(g => g.name === colourGroup);
			  })?.colour.code
			: undefined,
	);
	const [hoverColour, setHoveredColour] = useState<string>();

	const relevantParams: DesignParams = { ...(params || {}) };
	Object.keys(relevantParams).forEach(key => {
		if (!Object.values(DesignParamKeys).includes(key as DesignParamKeys)) {
			delete relevantParams[key as DesignParamKeys];
		}
	});

	const storeProductPrice = store?.productPrices?.find(p => p.product.id === product.id);
	let tile = (
		<>
			<div>
				<ProductTileImage
					webDomain={webDomain}
					productPath={productPath}
					product={product}
					activeColour={hoverColour || activeColour}
					hideSameday={!!store || (!!params?.decoration && params.decoration !== GQLProductSideAvailableDecorationType.Dtg)}
					{...ImageProps}
				/>
			</div>
			<div className="mt-4 lg:mt-8">
				{!hideBrand && (
					<div className="space-y-2 lg:space-y-3 mb-2 lg:mb-4">
						<p className="text-black uppercase font-belfast font-bold text-xs text-ellipsis whitespace-nowrap overflow-hidde">
							{brand?.name}
						</p>
						<p className="text-black font-display font-light lg:text-xl text-ellipsis whitespace-nowrap overflow-hidden">
							{name}
						</p>
					</div>
				)}

				{!hidePrice && (
					<div className="flex items-center space-x-1.5 mb-5 text-grey text-sm lg:text-base">
						<p className="mt-0.5">
							From{' '}
							{getCurrencyString(
								storeProductPrice?.salePrice ||
									(store ? getPartnerPortalPrice(product.salePrice, store) : product.salePrice),
							)}
						</p>
						<p className="text-xs">+ Decorations</p>
						{/* {!!calculator && <PriceCalculatorButton productCode={product.code} />} */}
					</div>
				)}
			</div>
		</>
	);

	if (onClick && hideLink) {
		tile = (
			<div onClick={onClick} className="group cursor-pointer">
				{tile}
			</div>
		);
	}

	//Empty string if we are at the root
	const baseUrl = location.pathname === '/' ? '' : location.pathname;

	return (
		<div className={cn('relative', className)}>
			{hideLink ? (
				tile
			) : (
				<Link
					onClick={onClick}
					className="group"
					to={`${productPath || `${baseUrl}/${product.slug}`}${getSearchString(relevantParams)}`}
					prefetch={reloadDocument ? 'none' : 'intent'}
					reloadDocument={reloadDocument}
				>
					{tile}
				</Link>
			)}
			{/* Desktop colors */}
			{!hideColour && (
				<ProductColourSwatch
					colours={product.colours}
					activeCode={hoverColour || activeColour}
					onClick={colour => setColour(colour?.colour.code)}
					hideNonSameday={hideNonSameday}
					hideLightningBolt={!!store || hideLightningBolt}
					onHover={colour => setHoveredColour(colour?.colour.code)}
					max={4}
					customColourIconHandler={customColourIconHandler}
				/>
			)}
		</div>
	);
}
